import React, { Fragment, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import {
  Box as MuiBox,
  Button,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography as MuiTypography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useStore } from "../../../store";
import Processing from "../../../components/Processing";
import { NoRowsOverlayWrap } from "../../../components/DataGrid/style";
import {
  KEYWORD_SORT_OPTIONS,
  KeywordSortBy,
  KeywordTemplate,
  PopularKeyword,
  displayKeyword,
  keywordPath,
  matchTypeLabel,
} from "../keywordParams";
import TemplateVideo from "./TemplateVideo";
import { TemplateStrip, TemplateCard, CardMeta, RatioBar } from "../styles";

const fmt = (n: number | null | undefined) => {
  if (n == null) return "N/A";
  return Number(n).toLocaleString();
};

/**
 * 카드에 보여줄 지표들. key 는 서버 sortBy 값과 같다.
 * 아이콘 대신 대문자 약자를 쓴다: V(view) L(like) D(download) S(share) AI.
 */
const METRICS: { key: KeywordSortBy; render: (t: KeywordTemplate) => string }[] = [
  { key: "view", render: (t) => `V ${fmt(t.viewCount)}` },
  { key: "like", render: (t) => `L ${fmt(t.likeCounts)}` },
  { key: "download", render: (t) => `D ${fmt(t.downloadCounts)}` },
  { key: "share", render: (t) => `S ${fmt(t.shareCounts)}` },
  {
    key: "aiScore",
    render: (t) => {
      if (!t.aiScore) return "AI N/A";
      return `AI ${t.aiScore.total}`;
    },
  },
];

/** 정렬 기준 지표는 첫 줄에 굵게, 나머지는 둘째 줄에 모아 보여준다. */
const primaryMetric = (t: KeywordTemplate, sortBy: string) =>
  (METRICS.find((m) => m.key === sortBy) ?? METRICS[0]).render(t);

const secondaryMetrics = (t: KeywordTemplate, sortBy: string) =>
  METRICS.filter((m) => m.key !== sortBy && m.key !== "aiScore")
    .slice(0, 3)
    .map((m) => m.render(t))
    .join(" · ");

/**
 * 정렬 선택 하나. 클릭하면 그 지표가 sortBy 가 되어 대표 템플릿과 드릴다운 목록이
 * 그 순서로 다시 조회된다. 서버는 내림차순만 지원하므로 활성 항목에는 아래 화살표만
 * 보여준다. 키워드 순위(templateCount 순)는 바뀌지 않는다.
 */
const SortChip = ({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: KeywordSortBy;
  active: boolean;
  onClick: (value: KeywordSortBy) => void;
}) => (
  <MuiBox
    component="button"
    type="button"
    onClick={() => onClick(value)}
    aria-pressed={active}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      border: "none",
      background: "none",
      p: "2px 6px",
      borderRadius: "4px",
      cursor: "pointer",
      font: "inherit",
      fontWeight: 700,
      color: active ? "#6750a4" : "#79747E",
      "&:hover": { color: "#6750a4", background: "rgba(103, 80, 164, 0.06)" },
    }}
  >
    {label}
    {active ? (
      <ArrowDownwardIcon fontSize="small" />
    ) : (
      <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.35 }} />
    )}
  </MuiBox>
);

const KeywordRow = ({
  rank,
  item,
  scanned,
  sortBy,
  matchType,
  onOpenKeyword,
}: {
  rank: number;
  item: PopularKeyword;
  scanned: number;
  sortBy: KeywordSortBy;
  matchType: string;
  onOpenKeyword: (keyword: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ratio = scanned > 0 ? item.templateCount / scanned : 0;
  const hasTemplates = item.templates.length > 0;
  // #, 키워드, 템플릿 수, 비율, Top N 토글
  const colSpan = 5;

  return (
    <Fragment>
      <TableRow hover sx={{ "& > td": { borderBottom: open ? "none" : undefined } }}>
        <TableCell align="center" sx={{ width: 56, color: "#79747E" }}>
          {rank}
        </TableCell>
        <TableCell>
          <Button
            variant="text"
            onClick={() => onOpenKeyword(item.keyword)}
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: "#6750a4",
              textTransform: "none",
              p: "2px 6px",
              minWidth: 0,
              justifyContent: "flex-start",
            }}
          >
            {displayKeyword(item.keyword, matchType)}
          </Button>
        </TableCell>
        <TableCell align="right" sx={{ width: 110, fontWeight: 600 }}>
          {fmt(item.templateCount)}
        </TableCell>
        <TableCell sx={{ width: 200 }}>
          <MuiBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RatioBar ratio={ratio} />
            <MuiTypography variant="body2" sx={{ color: "#49454F", minWidth: 52 }}>
              {(ratio * 100).toFixed(1)}%
            </MuiTypography>
          </MuiBox>
        </TableCell>
        <TableCell align="right" sx={{ width: 420 }}>
          {hasTemplates ? (
            <IconButton
              size="small"
              aria-label={open ? "hide templates" : "show templates"}
              onClick={() => setOpen((v) => !v)}
              sx={{ fontSize: 13, gap: 0.5, color: "#6750a4" }}
            >
              {open ? "Hide" : `Top ${item.templates.length}`}
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : (
            <MuiTypography variant="caption" sx={{ color: "#79747E" }}>
              —
            </MuiTypography>
          )}
        </TableCell>
      </TableRow>
      {hasTemplates && (
        <TableRow>
          <TableCell
            colSpan={colSpan}
            sx={{ p: 0, borderBottom: open ? undefined : "none" }}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <TemplateStrip>
                {item.templates.map((t) => (
                  <Tooltip key={t.templateId} title={t.description || ""} arrow>
                    <TemplateCard
                      onClick={() => navigate(`/templates/${t.templateId}`)}
                    >
                      <TemplateVideo src={t.videoPath} height={160} maxWidth={138} />
                      <CardMeta sx={{ fontWeight: 600, color: "#1C1B1F" }}>
                        {primaryMetric(t, sortBy)}
                      </CardMeta>
                      <CardMeta>{secondaryMetrics(t, sortBy)}</CardMeta>
                      <CardMeta title={t.description}>{t.description}</CardMeta>
                    </TemplateCard>
                  </Tooltip>
                ))}
              </TemplateStrip>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

const PopularTable = () => {
  const {
    KeywordsStore: { popular, sortBy, sortPopularBy, templatesPerKeyword, openKeyword },
  } = useStore();
  const navigate = useNavigate();

  const handleOpenKeyword = (keyword: string) => {
    openKeyword(keyword);
    // 해시태그 목록에서 열었으면 URL 에 # 을 남겨 유형이 함께 이동한다.
    navigate(keywordPath(keyword, popular.matchType));
  };

  if (popular.loading) {
    return (
      <NoRowsOverlayWrap style={{ height: 240 }}>
        <Processing size={40} color="grey" label="loading" />
      </NoRowsOverlayWrap>
    );
  }

  if (popular.fetched && popular.keywords.length === 0) {
    return (
      <NoRowsOverlayWrap style={{ height: 240 }}>
        No keywords were found for this country and period.
      </NoRowsOverlayWrap>
    );
  }

  return (
    <Table size="small" sx={{ "& th": { fontWeight: 700, color: "#49454F" } }}>
      <TableHead>
        <TableRow>
          <TableCell align="center">#</TableCell>
          <TableCell>{matchTypeLabel(popular.matchType)}</TableCell>
          <TableCell align="right">Templates</TableCell>
          <TableCell>Share of scanned</TableCell>
          {/* 마지막 컬럼 헤더가 정렬 선택이다. 본문 셀의 "Top N" 을 펼치면 이 순서로
              대표 템플릿이 보이고, 키워드를 눌러 들어간 목록도 같은 순서다. */}
          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
            <MuiBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                justifyContent: "flex-end",
              }}
            >
              <MuiTypography
                component="span"
                sx={{ fontSize: 12, color: "#79747E", mr: "4px" }}
              >
                Top {templatesPerKeyword} by
              </MuiTypography>
              {KEYWORD_SORT_OPTIONS.map((o) => (
                <SortChip
                  key={o.value}
                  label={o.label}
                  value={o.value}
                  active={o.value === sortBy}
                  onClick={sortPopularBy}
                />
              ))}
            </MuiBox>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {popular.keywords.map((item, i) => (
          <KeywordRow
            key={item.keyword}
            rank={i + 1}
            item={item}
            scanned={popular.scannedTemplates}
            sortBy={sortBy}
            matchType={popular.matchType}
            onOpenKeyword={handleOpenKeyword}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default observer(PopularTable);
